/**
 * ForecastSuggestions.tsx — Forecast programme F3 (register row 71): the
 * Suggested categories card, spec §3.3, rendered from forecastSuggestions.ts's
 * model. The rules, the refusal of Accept all and the confirmed-not-optimistic
 * swap are stated in that file's header.
 *
 * Accept (one deal) → PUT /v1/sales/deals/{id} {forecastCategory,
 * acceptSuggestionRuleId} → on the server's 200 the button swaps to "✓ Set",
 * a 5 s toast offers Undo (PUT {clearForecastCategory}), and `onChanged`
 * refetches the forecast so the hero and roll-ups recalculate from the wire.
 * A refusal (403 role, 409 closed or stale) is a toast with the server's
 * sentence, and the forecast is refetched so a stale row goes away.
 */
import { useEffect, useRef, useState } from 'react'
import { Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import type { SalesForecast } from '../../../../api/types'
import { Avatar } from '../../DashboardShared'
import { updateDeal } from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import { forecastSuggestionPill, success, warning } from '../../../../theme/tokens'
import {
  type SuggestionRow, suggestionRows, suggestionsSubtitle, mergeRows, acceptedToast, undoneToast, UNDO_MS,
} from './forecastSuggestions'

type RowState = 'pending' | 'saving' | 'set' | 'undoing'

export function ForecastSuggestions({ data, onChanged, onSelectDeal }: {
  data: SalesForecast
  onChanged?: () => void
  onSelectDeal?: (id: string) => void
}) {
  const wire = suggestionRows(data)
  const [rows, setRows] = useState<SuggestionRow[]>(() => wire ?? [])
  const [state, setState] = useState<Record<string, RowState>>({})
  const stateRef = useRef(state)
  stateRef.current = state

  // A refetch brings a new response: keep this session's accepted rows in
  // place, take everything else from the wire.
  useEffect(() => {
    if (!wire) return
    const accepted = new Set(Object.entries(stateRef.current).filter(([, s]) => s === 'set' || s === 'undoing').map(([id]) => id))
    setRows(prev => mergeRows(prev, wire, accepted))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  if (!wire) return null
  const pending = wire.length
  if (rows.length === 0) return null

  const mark = (id: string, s: RowState | undefined) =>
    setState(prev => {
      const next = { ...prev }
      if (s) next[id] = s
      else delete next[id]
      return next
    })

  const undo = async (r: SuggestionRow) => {
    mark(r.dealId, 'undoing')
    try {
      await updateDeal(r.dealId, { clearForecastCategory: true })
      mark(r.dealId, undefined)
      toast.success(undoneToast(r))
    } catch (e) {
      mark(r.dealId, 'set')
      toast.error(apiErrorMessage(e, 'Failed to undo the category'))
    }
    onChanged?.()
  }

  const accept = async (r: SuggestionRow) => {
    mark(r.dealId, 'saving')
    try {
      await updateDeal(r.dealId, { forecastCategory: r.category, acceptSuggestionRuleId: r.ruleId })
      mark(r.dealId, 'set')
      toast.success(acceptedToast(r), { duration: UNDO_MS, action: { label: 'Undo', onClick: () => { void undo(r) } } })
    } catch (e) {
      mark(r.dealId, undefined)
      toast.error(apiErrorMessage(e, 'Failed to accept the suggestion'))
    }
    onChanged?.()
  }

  return (
    <section className="k-card" aria-label="Suggested categories">
      <div className="p-5 flex flex-col gap-4">
        <div>
          <div className="flex items-center gap-2 text-13 font-semibold text-ink-primary">
            <Sparkles size={14} aria-hidden="true" />Suggested categories
          </div>
          <div className="text-12 text-ink-muted mt-1" data-suggestions-pending={pending}>{suggestionsSubtitle(pending)}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-13">
            <thead>
              <tr className="text-left text-12 text-ink-muted">
                <th className="font-medium py-2 pr-3">Deal</th>
                <th className="font-medium py-2 pr-3">Owner</th>
                <th className="font-medium py-2 pr-3">Stage</th>
                <th className="font-medium py-2 pr-3 text-right">Value</th>
                <th className="font-medium py-2 pr-3">Suggestion</th>
                <th className="font-medium py-2 text-right"><span className="sr-only">Action</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const s = state[r.dealId] ?? 'pending'
                const pill = forecastSuggestionPill[r.category as keyof typeof forecastSuggestionPill]
                return (
                  <tr key={r.dealId} className="border-t border-edge-row align-top" data-suggestion-deal={r.dealId} data-suggestion-rule={r.ruleId} data-suggestion-state={s}>
                    <td className="py-2 pr-3 min-w-0">
                      {onSelectDeal
                        ? <button type="button" className="text-left font-medium text-ink-primary hover:underline" onClick={() => onSelectDeal(r.dealId)}>{r.title}</button>
                        : <div className="font-medium text-ink-primary">{r.title}</div>}
                      <div className="text-12" style={{ color: r.closeWarn ? warning.text : undefined }}>
                        <span className={r.closeWarn ? '' : 'text-ink-muted'}>{r.closeLine}</span>
                      </div>
                    </td>
                    <td className="py-2 pr-3">
                      {r.ownerName
                        ? <span className="inline-flex items-center gap-2"><Avatar name={r.ownerName} size={22} /><span className="text-ink-body">{r.ownerName}</span></span>
                        : <span className="text-ink-muted">No linked owner</span>}
                    </td>
                    <td className="py-2 pr-3 text-ink-body">{r.stageLabel}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-ink-primary" title={r.valueExact}>{r.valueText}</td>
                    <td className="py-2 pr-3">
                      <span className="inline-flex items-center rounded-full px-2 py-[2px] text-12 font-semibold"
                        style={pill ? { background: pill.bg, color: pill.fg } : undefined}>{r.categoryLabel}</span>
                      <div className="text-12 text-ink-muted mt-1">{r.reason}</div>
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      {s === 'set' || s === 'undoing'
                        ? <span className="text-13 font-semibold" style={{ color: success.text }}>✓ Set</span>
                        : <button type="button" className="k-btn" disabled={s === 'saving'}
                            aria-label={`Accept ${r.categoryLabel} for ${r.title}`} onClick={() => { void accept(r) }}>
                            {s === 'saving' ? 'Accepting…' : 'Accept'}
                          </button>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}
